import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  fullTextSearch,
  generateQueryEmbedding,
  hybridSearch,
  autocomplete,
  getPopularSearches,
  getTrendingTopics,
  getFallbackResults,
} from '@/services/search';
import type { SearchFilters, SearchContext, SearchResultItem } from '@/services/search';

const STALE_10MIN = 10 * 60 * 1000;

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useSearchResults(query: string, context?: SearchContext, filters?: SearchFilters) {
  const debouncedQuery = useDebounce(query, 300);

  return useQuery({
    queryKey: ['search', 'results', debouncedQuery, context, filters],
    queryFn: async (): Promise<SearchResultItem[]> => {
      const ftsResults = await fullTextSearch(debouncedQuery, context, filters);

      const embedding = await generateQueryEmbedding(debouncedQuery);
      if (embedding) {
        const hybridResults = await hybridSearch(debouncedQuery, embedding, filters);
        if (hybridResults.length > 0) return hybridResults;
      }

      return ftsResults;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: STALE_10MIN,
  });
}

export function useAutocomplete(prefix: string) {
  const debouncedPrefix = useDebounce(prefix, 150);

  return useQuery({
    queryKey: ['search', 'autocomplete', debouncedPrefix],
    queryFn: () => autocomplete(debouncedPrefix),
    enabled: debouncedPrefix.length >= 2,
    staleTime: STALE_10MIN,
  });
}

export function usePopularSearches() {
  return useQuery({
    queryKey: ['search', 'popular'],
    queryFn: () => getPopularSearches(),
    staleTime: STALE_10MIN,
  });
}

export function useTrendingTopics() {
  return useQuery({
    queryKey: ['search', 'trending'],
    queryFn: () => getTrendingTopics(),
    staleTime: STALE_10MIN,
  });
}

export function useFallbackResults() {
  return useQuery({
    queryKey: ['search', 'fallback'],
    queryFn: () => getFallbackResults(),
    staleTime: STALE_10MIN,
  });
}
